import {
  LegacyProductCode,
  ProductBehavior,
  ProductForm,
  ProductMaterial,
} from '@up2stay/up2promo-api-client';
import type { ProductDto } from '@up2stay/up2promo-api-client';

import type { ProductFormValues } from './product-form.types';
import {
  PRODUCT_KIND_OPTIONS,
  type ProductKindLabels,
} from './products.constants';

/** Physical form / material labels, keyed like the app's `products.identity` dictionary. */
export type ProductIdentityLabels = {
  formStand?: string;
  formCard?: string;
  formBadge?: string;
  formUnknown?: string;
  materialPvc?: string;
  materialMetal?: string;
  materialAcryl?: string;
  materialGeneric?: string;
};

const INTENDED_TO_SHORT: Record<ProductBehavior, LegacyProductCode> = {
  [ProductBehavior.GOOGLE_REVIEW]: LegacyProductCode.GO,
  [ProductBehavior.INSTAGRAM]: LegacyProductCode.IG,
  [ProductBehavior.FACEBOOK]: LegacyProductCode.FB,
  [ProductBehavior.TIKTOK]: LegacyProductCode.TT,
  [ProductBehavior.MENU]: LegacyProductCode.ME,
  [ProductBehavior.PROFILE]: LegacyProductCode.BC,
  [ProductBehavior.URL]: LegacyProductCode.CU,
};

const SHORT_TO_INTENDED: Record<string, ProductBehavior> = {
  [LegacyProductCode.GO]: ProductBehavior.GOOGLE_REVIEW,
  [LegacyProductCode.IG]: ProductBehavior.INSTAGRAM,
  [LegacyProductCode.FB]: ProductBehavior.FACEBOOK,
  [LegacyProductCode.TT]: ProductBehavior.TIKTOK,
  [LegacyProductCode.ME]: ProductBehavior.MENU,
  [LegacyProductCode.BC]: ProductBehavior.PROFILE,
  [LegacyProductCode.CU]: ProductBehavior.URL,
};

const LONG_TO_INTENDED: Record<string, ProductBehavior> = {
  [ProductBehavior.GOOGLE_REVIEW]: ProductBehavior.GOOGLE_REVIEW,
  [ProductBehavior.INSTAGRAM]: ProductBehavior.INSTAGRAM,
  [ProductBehavior.FACEBOOK]: ProductBehavior.FACEBOOK,
  [ProductBehavior.TIKTOK]: ProductBehavior.TIKTOK,
  [ProductBehavior.MENU]: ProductBehavior.MENU,
  [ProductBehavior.PROFILE]: ProductBehavior.PROFILE,
  [ProductBehavior.URL]: ProductBehavior.URL,
};

function mapToIntendedBehavior(
  value?: string | null,
): ProductBehavior | undefined {
  if (!value) return undefined;
  return (
    SHORT_TO_INTENDED[value] ??
    LONG_TO_INTENDED[value] ??
    (Object.values(ProductBehavior).includes(value as ProductBehavior)
      ? (value as ProductBehavior)
      : undefined)
  );
}

/**
 * Factory intent for create/update writes.
 * Locked knownProductType wins even when routing is customUrl (type=URL).
 * CU / free select: derive from chosen targetKind.
 */
export function resolveIntendedBehaviorForFormWrite(
  values: Pick<ProductFormValues, 'targetKind'>,
  knownProductType?: string | null,
): ProductBehavior {
  if (knownProductType && knownProductType !== LegacyProductCode.CU) {
    return mapToIntendedBehavior(knownProductType) ?? ProductBehavior.URL;
  }
  return mapToIntendedBehavior(values.targetKind) ?? ProductBehavior.URL;
}

/** Physical form for list/detail UI (factory identity). */
export function resolvePhysicalForm(
  product: Pick<ProductDto, 'item'>,
): ProductForm | undefined {
  return product.item.form;
}

/** Map long intendedBehavior → short productType for badges/chips. */
export function intendedBehaviorToProductType(
  intendedBehavior: string,
): LegacyProductCode | undefined {
  if (
    Object.values(ProductBehavior).includes(intendedBehavior as ProductBehavior)
  ) {
    return INTENDED_TO_SHORT[intendedBehavior as ProductBehavior];
  }
  return undefined;
}

/**
 * Short code for behaviour chip icons/labels: the factory behaviour, else
 * the target kind.
 */
export function resolveBehaviorProductType(
  product: Pick<ProductDto, 'item' | 'target'>,
): string {
  const behavior = product.item.behavior ?? product.target?.kind;
  return behavior
    ? (INTENDED_TO_SHORT[behavior] ?? LegacyProductCode.CU)
    : LegacyProductCode.CU;
}

export function getProductKindLabel(
  productType: string | undefined,
  labels: ProductKindLabels | undefined,
): string {
  const opt = PRODUCT_KIND_OPTIONS.find((o) => o.value === productType);
  return (opt ? labels?.[opt.labelKey] : productType) ?? '—';
}

export function getBehaviorKindLabel(
  product: Pick<ProductDto, 'item' | 'target'>,
  labels: ProductKindLabels | undefined,
): string {
  return getProductKindLabel(resolveBehaviorProductType(product), labels);
}

export function getPhysicalFormLabel(
  form: ProductForm | undefined,
  labels: ProductIdentityLabels | undefined,
): string {
  if (!form) return labels?.formUnknown ?? '—';
  switch (form) {
    case ProductForm.STAND:
      return labels?.formStand ?? 'Stand';
    case ProductForm.CARD:
      return labels?.formCard ?? 'Business card';
    case ProductForm.BADGE:
      return labels?.formBadge ?? 'Badge';
    default:
      return labels?.formUnknown ?? '—';
  }
}

export function getPhysicalMaterialLabel(
  material: ProductMaterial | undefined,
  labels: ProductIdentityLabels | undefined,
): string | undefined {
  if (!material) return undefined;
  switch (material) {
    case ProductMaterial.PVC:
      return labels?.materialPvc ?? 'PVC';
    case ProductMaterial.METAL:
      return labels?.materialMetal ?? 'Metal';
    case ProductMaterial.ACRYL:
      return labels?.materialAcryl ?? 'Acrylic';
    case ProductMaterial.GENERIC:
      return labels?.materialGeneric ?? 'Generic';
    default:
      return material;
  }
}
